import { errorMessage } from '@runnerboy/api-client';
import { SUPPORTED_LOCALES } from '@runnerboy/i18n';
import type { AddressDto } from '@runnerboy/types';
import { Alert, Badge, Button, Card, cx, EmptyState, Field, Input, PageHeader, Select, Sheet, Switch, Textarea, useToast } from '@runnerboy/ui';
import { Bell, Building2, CheckCircle2, ChevronRight, Download, FileText, Hotel, LifeBuoy, LogOut, MapPin, MessageSquare, Receipt, ShieldCheck, Trash2, User } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { AgeVerifySheet } from '../components/sheets';
import { Page } from '../components/shell';
import { setLocale } from '../lib/i18n';
import { apiOrigin, isNative, SERVER_OVERRIDE_ALLOWED, setServerOverride } from '../lib/native';
import { client, useAsync, useSession } from '../lib/session';

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '1.0.0-rc.1';

interface Me {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  locale: string;
  ageVerification: { verified: boolean; verifiedAt: string | null; method: string | null };
  notificationPrefs: { orderUpdates: boolean; sms: boolean; email: boolean } | null;
}

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const { user, setLoginOpen, logout } = useSession();
  const toast = useToast();
  const me = useAsync((s) => client.get<Me>('/me', s), [user?.id], { enabled: !!user });
  const addresses = useAsync((s) => client.get<AddressDto[]>('/me/addresses', s), [user?.id], { enabled: !!user });
  const [name, setName] = useState('');
  const [ageOpen, setAgeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  useEffect(() => setName(me.data?.name ?? ''), [me.data?.name]);

  if (!user) {
    return (
      <Page>
        <EmptyState icon={<User className="h-8 w-8" />} title={t('auth.loginTitle')} action={<Button onClick={() => setLoginOpen(true)}>{t('common.continue')}</Button>} />
        <LanguagePicker />
        <SafetyLink />
        <AppInfo />
      </Page>
    );
  }
  const saveProfile = async (patch: Record<string, unknown>) => {
    try {
      me.setData(await client.patch<Me>('/me', patch));
      toast('success', t('common.save'));
    } catch (e) {
      toast('danger', errorMessage(e));
    }
  };
  const exportData = async () => {
    const data = await client.get<unknown>('/me/export');
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'runnerboy-my-data.json';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const verified = !!me.data?.ageVerification.verified;
  const display = me.data?.name?.trim() || t('account.title');
  return (
    <Page>
      <section className="relative mt-1 overflow-hidden rounded-[28px] bg-night-900 p-5 text-white shadow-[var(--shadow-float)]">
        <span className="pointer-events-none absolute -right-12 -top-16 h-44 w-44 rounded-full bg-marigold-500/20 blur-3xl" aria-hidden />
        <div className="relative flex items-center gap-4">
          <span className="rb-display grid h-16 w-16 shrink-0 place-items-center rounded-full bg-marigold-500 text-[26px] font-extrabold text-ink-950 shadow-[var(--shadow-glow)]">{display.slice(0, 1).toUpperCase()}</span>
          <div className="min-w-0">
            <h1 className="rb-display truncate text-[24px] font-bold leading-tight">{display}</h1>
            <div className="rb-num mt-0.5 text-[13px] text-white/60">{me.data?.phone ?? ''}</div>
          </div>
        </div>
        <div className="relative mt-4 flex items-center gap-2">
          {verified ? (
            <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-white/10 px-3 text-[12.5px] font-semibold text-white ring-1 ring-white/15">
              <CheckCircle2 className="h-4 w-4 text-success-500" aria-hidden /> {t('checkout.ageVerified')}
            </span>
          ) : (
            <Button size="sm" variant="accent" onClick={() => setAgeOpen(true)}>
              <ShieldCheck className="h-4 w-4" /> {t('checkout.verifyAge')}
            </Button>
          )}
          <Link to="/orders" className="rb-press inline-flex h-8 items-center gap-1.5 rounded-full bg-white/10 px-3 text-[12.5px] font-semibold text-white ring-1 ring-white/15">
            <Receipt className="h-4 w-4" aria-hidden /> {t('nav.orders', 'Orders')}
          </Link>
        </div>
      </section>

      <Group title={t('account.profile')}>
        <form
          className="flex gap-2 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void saveProfile({ name });
          }}
        >
          <Input aria-label={t('account.name')} placeholder={t('account.name')} value={name} onChange={(e) => setName(e.target.value)} className="rounded-full" />
          <Button type="submit" variant="primary" disabled={name.trim() === (me.data?.name ?? '')}>
            {t('common.save')}
          </Button>
        </form>
      </Group>

      <Group title={t('account.addresses')}>
        {(addresses.data ?? []).length === 0 && <p className="p-4 text-[13.5px] text-ink-500">{t('address.noAddresses')}</p>}
        {(addresses.data ?? []).map((a) => {
          const Icon = a.label === 'HOTEL' ? Hotel : a.label === 'WORK' ? Building2 : MapPin;
          return (
            <div key={a.id} className="flex items-center gap-3 px-4 py-3">
              <RowIcon>
                <Icon className="h-[18px] w-[18px]" />
              </RowIcon>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-ink-900">{t(`address.${a.label}`)}</div>
                <div className="truncate text-[12px] text-ink-500">{[a.flat, a.building, a.locality, a.city, a.pincode].filter(Boolean).join(', ')}</div>
              </div>
              <button
                className="rb-press grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-400 hover:bg-danger-50 hover:text-danger-600"
                aria-label={`${t('common.delete')} ${t(`address.${a.label}`)}`}
                onClick={async () => {
                  await client.del(`/me/addresses/${a.id}`);
                  addresses.reload();
                }}
              >
                <Trash2 className="h-[18px] w-[18px]" />
              </button>
            </div>
          );
        })}
      </Group>

      <Group title={t('account.notifications')}>
        {(['orderUpdates', 'sms'] as const).map((k) => (
          <div key={k} className="flex items-center gap-3 px-4 py-3">
            <RowIcon>{k === 'sms' ? <MessageSquare className="h-[18px] w-[18px]" /> : <Bell className="h-[18px] w-[18px]" />}</RowIcon>
            <span className="flex-1 text-[14px] font-semibold text-ink-800">{t(`account.${k}`)}</span>
            <Switch
              label={t(`account.${k}`)}
              checked={me.data?.notificationPrefs?.[k] ?? true}
              onChange={(v) => void saveProfile({ notificationPrefs: { orderUpdates: true, sms: true, email: false, ...(me.data?.notificationPrefs ?? {}), [k]: v } })}
            />
          </div>
        ))}
      </Group>

      <LanguagePicker onChange={(code) => void saveProfile({ locale: code })} current={i18n.language} />

      <SafetyLink />
      <Group title={t('account.help', 'Help & legal')}>
        <LinkRow to="/support" icon={<LifeBuoy className="h-[18px] w-[18px]" />} label={t('common.support')} />
        <LinkRow to="/legal" icon={<FileText className="h-[18px] w-[18px]" />} label={t('account.legal')} />
      </Group>
      <Group title={t('account.yourData', 'Your data')}>
        <button onClick={() => void exportData()} className="rb-press flex w-full items-center gap-3 px-4 py-3 text-left">
          <RowIcon>
            <Download className="h-[18px] w-[18px]" />
          </RowIcon>
          <span className="flex-1 text-[14px] font-semibold text-ink-800">{t('account.exportData')}</span>
          <ChevronRight className="h-4 w-4 text-ink-300" />
        </button>
        <button onClick={() => setDeleteOpen(true)} className="rb-press flex w-full items-center gap-3 px-4 py-3 text-left">
          <RowIcon danger>
            <Trash2 className="h-[18px] w-[18px]" />
          </RowIcon>
          <span className="flex-1 text-[14px] font-semibold text-danger-600">{t('account.deleteAccount')}</span>
          <ChevronRight className="h-4 w-4 text-danger-600/60" />
        </button>
      </Group>
      <Button variant="secondary" block className="mt-5" onClick={() => void logout()}>
        <LogOut className="h-4 w-4" /> {t('common.logout')}
      </Button>
      <AppInfo />
      <AgeVerifySheet open={ageOpen} onClose={() => setAgeOpen(false)} onVerified={() => { setAgeOpen(false); me.reload(); }} />
      <Sheet open={deleteOpen} onClose={() => setDeleteOpen(false)} title={t('account.deleteAccount')}>
        <p className="text-sm text-ink-600">{t('account.deleteBody')}</p>
        <Button
          variant="danger"
          block
          size="lg"
          className="mt-4"
          onClick={async () => {
            try {
              await client.post('/me/deletion-request', {});
              toast('success', t('account.deleteAccount'));
              setDeleteOpen(false);
              await logout();
            } catch (e) {
              toast('danger', errorMessage(e));
            }
          }}
        >
          {t('account.deleteAccount')}
        </Button>
      </Sheet>
    </Page>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="rb-eyebrow mb-2 px-1 text-ink-500">{title}</h2>
      <div className="divide-y divide-ink-900/[0.06] overflow-hidden rounded-[22px] bg-white shadow-[var(--shadow-card)]">{children}</div>
    </section>
  );
}

function RowIcon({ children, danger }: { children: ReactNode; danger?: boolean }) {
  return <span className={cx('grid h-9 w-9 shrink-0 place-items-center rounded-full', danger ? 'bg-danger-50 text-danger-600' : 'bg-ink-900/[0.05] text-ink-700')}>{children}</span>;
}

function LinkRow({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  return (
    <Link to={to} className="rb-press flex items-center gap-3 px-4 py-3">
      <RowIcon>{icon}</RowIcon>
      <span className="flex-1 text-[14px] font-semibold text-ink-800">{label}</span>
      <ChevronRight className="h-4 w-4 text-ink-300" />
    </Link>
  );
}

function LanguagePicker({ onChange, current }: { onChange?: (code: string) => void; current?: string }) {
  const { t, i18n } = useTranslation();
  return (
    <Card className="mt-6 rounded-[22px] p-4">
      <Field label={t('common.language')} htmlFor="lang">
        <Select
          id="lang"
          value={current ?? i18n.language}
          onChange={(e) => {
            setLocale(e.target.value);
            onChange?.(e.target.value);
          }}
        >
          {SUPPORTED_LOCALES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.nativeLabel} ({l.label})
            </option>
          ))}
        </Select>
      </Field>
    </Card>
  );
}

const CATEGORIES = ['MISSING_ITEM', 'WRONG_ITEM', 'DAMAGED', 'LATE_DELIVERY', 'RUNNER_ISSUE', 'STORE_ISSUE', 'PAYMENT', 'REFUND', 'AGE_VERIFICATION', 'ACCOUNT', 'OTHER'] as const;

export function SupportPage() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const { user, setLoginOpen } = useSession();
  const toast = useToast();
  const tickets = useAsync((s) => client.get<{ id: string; code: string; subject: string; status: string; createdAt: string }[]>('/support/tickets', s), [user?.id], { enabled: !!user });
  const [form, setForm] = useState({ category: 'OTHER' as (typeof CATEGORIES)[number], subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  if (!user) return <Page><EmptyState title={t('auth.loginTitle')} action={<Button onClick={() => setLoginOpen(true)}>{t('common.continue')}</Button>} /></Page>;
  return (
    <Page>
      <PageHeader title={t('support.title')} />
      <Card className="mt-4 p-4">
        <h2 className="text-sm font-bold">{t('support.newTicket')}</h2>
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              const r = await client.post<{ code: string }>('/support/tickets', { ...form, orderId: sp.get('order') ?? undefined });
              toast('success', t('support.submitted', { code: r.code }));
              setForm({ category: 'OTHER', subject: '', message: '' });
              tickets.reload();
            } catch (err) {
              toast('danger', errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label={t('support.category')} htmlFor="cat">
            <Select id="cat" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as (typeof CATEGORIES)[number] }))}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`support.${c}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('support.subject')} htmlFor="subj">
            <Input id="subj" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} maxLength={140} />
          </Field>
          <Field label={t('support.message')} htmlFor="msg">
            <Textarea id="msg" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} maxLength={4000} />
          </Field>
          {sp.get('order') && <Alert tone="info">Linked to your order.</Alert>}
          <Button type="submit" loading={busy} disabled={form.subject.length < 3 || form.message.length < 3}>
            {t('support.submit')}
          </Button>
        </form>
      </Card>
      <div className="mt-4 flex flex-col gap-2">
        {(tickets.data ?? []).map((tk) => (
          <Card key={tk.id} className="flex items-center justify-between p-4">
            <div>
              <div className="text-sm font-semibold">{tk.subject}</div>
              <div className="text-xs text-ink-500">{tk.code}</div>
            </div>
            <Badge>{tk.status.replace(/_/g, ' ')}</Badge>
          </Card>
        ))}
      </div>
    </Page>
  );
}

const UPDATED = '16 October 2026';

function LegalSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 rounded-[22px] bg-white p-5 shadow-[var(--shadow-card)]">
      <h2 className="rb-display text-[19px] font-bold text-ink-900">{title}</h2>
      <div className="mt-2 space-y-2.5 text-[14px] leading-relaxed text-ink-700 [&_li]:ml-4 [&_li]:list-disc [&_strong]:text-ink-900">{children}</div>
    </section>
  );
}

/**
 * Privacy policy + terms (public: https://runnerboy.app/privacy and /terms open without onboarding).
 * Facts here mirror the implementation: retention values come from configuration defaults (location history
 * 30 days, ID documents never copied), ad events are stored only as aggregated counts per creative.
 */
export function LegalPage() {
  const disclosures = useAsync((s) => client.get<{ environment: string }>('/public/config', s), []);
  const env = disclosures.data?.environment;
  return (
    <Page>
      <PageHeader title="Privacy & terms" subtitle={`Runner Boy · last updated ${UPDATED}`} />
      {env && env !== 'production' && (
        <Alert tone="warning" className="mt-4">
          This is a {env} environment: stores, orders and payments are simulated. This text is a framework pending legal review before launch.
        </Alert>
      )}
      <nav className="mt-4 flex flex-wrap gap-2 text-[13px] font-semibold" aria-label="Sections">
        {[
          ['privacy', 'Privacy policy'],
          ['location', 'Location'],
          ['retention', 'Retention & your rights'],
          ['terms', 'Terms of use'],
          ['contact', 'Contact & grievances'],
        ].map(([id, label]) => (
          <a key={id} href={`#${id}`} className="rb-press rounded-full bg-white px-3.5 py-2 text-ink-800 shadow-[var(--shadow-card)]">
            {label}
          </a>
        ))}
      </nav>
      <div className="mt-4 flex flex-col gap-3">
        <LegalSection id="privacy" title="Privacy policy">
          <p>
            Runner Boy is a technology and delivery platform that connects you with <strong>licensed neighbourhood retailers</strong> and verified delivery runners. This policy explains what we collect, why, and the choices you have.
          </p>
          <p>
            <strong>Customers.</strong> Your mobile number (sign-in by one-time code), optional name, delivery addresses and their map position, your date of birth for age verification (stored encrypted), orders and
            receipts, support tickets and messages with your runner, notification preferences, and session/device information used to keep you signed in securely.
          </p>
          <p>
            <strong>Payments</strong> are processed by the payment provider. We receive the payment status and reference — never your full card or UPI credentials.
          </p>
          <p>
            <strong>Age check at the door.</strong> The runner checks an original ID in person. We record only the result, the document type and a masked reference — <strong>never a copy or photo of your ID</strong>.
          </p>
          <p>
            <strong>Runners.</strong> Mobile number, name, identity and vehicle document details checked by operations during onboarding (any copies are kept in private, access-controlled storage), shift status, deliveries and earnings, and location while on shift or delivering (see below).
          </p>
          <p>
            <strong>Stores.</strong> Business and licence details, staff mobile numbers and roles, inventory, orders and settlements.
          </p>
          <p>
            <strong>How we use it:</strong> to take and deliver orders; to apply the law where you are (legal drinking age, sale hours, dry days, quantity limits, no inter-state delivery); for safety, fraud prevention and support; for
            store settlements and runner payouts; and to improve the service.
          </p>
          <p>
            <strong>Who sees what:</strong> the licensed store sees your order and delivery area; your runner sees your first name and, only after accepting, your address and delivery instructions; the payment provider processes
            payment; authorities only where the law requires it. <strong>We do not sell personal data.</strong>
          </p>
          <p>
            <strong>Advertising</strong> inside the app is first-party. Impressions and clicks are counted per advertisement, not linked to your identity. We use no third-party advertising networks or cross-app tracking. Alcohol-brand
            advertising is shown only where it is legally permitted and only to adults who have confirmed their age.
          </p>
        </LegalSection>
        <LegalSection id="location" title="Location">
          <ul>
            <li>
              <strong>Customer app:</strong> your device location is used only if you allow it, to suggest a delivery address and find licensed stores near you. You can always enter an address instead.
            </li>
            <li>
              <strong>Runner app:</strong> collects location <strong>in the background only while the runner is on shift or delivering</strong>, with a permanent notification on the phone. It is used to offer nearby deliveries, show
              the delivery's progress to its customer and store, and for safety (SOS). Tracking stops when the shift ends.
            </li>
            <li>
              A customer sees the runner's position <strong>only for their own active order</strong>, and it is hidden as soon as the order is complete. Location history is deleted after 30 days.
            </li>
          </ul>
        </LegalSection>
        <LegalSection id="retention" title="Retention & your rights">
          <ul>
            <li>Orders and invoices are kept for the periods required by tax and excise law.</li>
            <li>Runner location history: 30 days. Copies of ID documents: none are kept.</li>
            <li>
              You can <strong>download your data</strong> or <strong>request deletion</strong> from Account. Deletion anonymises your profile; records the law requires us to keep are retained without your personal identifiers.
            </li>
            <li>Data is encrypted in transit (HTTPS) and sensitive fields at rest; staff access requires multi-factor authentication and is audited.</li>
            <li>Runner Boy is not for anyone below the legal drinking age where they live.</li>
          </ul>
        </LegalSection>
        <LegalSection id="terms" title="Terms of use">
          <p>
            <strong>Who sells what.</strong> Alcohol is sold by the licensed retailer named on your order, who issues the statutory invoice. Runner Boy provides the technology platform and delivery service and shows its delivery and
            platform fees separately.
          </p>
          <p>
            <strong>Age & identity.</strong> You must be of the legal drinking age for your delivery address and show an original ID at the door. No ID, no alcohol — non-alcoholic items can still be handed over. Runners may refuse a
            handover to anyone who appears intoxicated or underage.
          </p>
          <p>
            <strong>Local rules.</strong> Sale hours, dry days, quantity limits and state borders are enforced automatically. Delivery is available only in cities where the required licences and approvals are documented.
          </p>
          <p>
            <strong>Cancellations & refunds.</strong> Free cancellation until the store accepts; a small fee may apply afterwards. If delivery cannot be completed (underage, unavailable, no valid ID), the sealed order returns to the
            store and the items are refunded; delivery fees may be retained.
          </p>
          <p>
            <strong>Road safety.</strong> Don’t drink and drive. Runner Boy exists so that you can order from your place and stay safe.
          </p>
        </LegalSection>
        <LegalSection id="contact" title="Contact & grievances">
          <p>
            Use <Link to="/support" className="font-semibold underline">Help</Link> in the app to reach support about an order, your account or this policy. The grievance officer’s name and contact details (Consumer Protection
            (E-Commerce) Rules) are published here before launch.
          </p>
        </LegalSection>
      </div>
    </Page>
  );
}

function SafetyLink() {
  const { t } = useTranslation();
  return (
    <Link to="/road-safety" className="rb-press relative mt-6 flex items-center gap-3 overflow-hidden rounded-[22px] bg-night-900 p-4 text-white shadow-[var(--shadow-card)]">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-marigold-500 text-ink-950">
        <ShieldCheck className="h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="rb-display block text-[15px] font-extrabold leading-tight text-marigold-500">{t('safety.line1', 'DON’T DRINK & DRIVE.')}</span>
        <span className="block text-[12px] text-white/65">{t('safety.title', 'Road safety')}</span>
      </span>
      <ChevronRight className="h-4 w-4 text-white/60" aria-hidden />
    </Link>
  );
}

/** App version and, in demo builds of the Android app only, the API server (e.g. switch from USB to the HTTPS demo). */
function AppInfo() {
  const { t } = useTranslation();
  const [server, setServer] = useState(apiOrigin());
  const [saved, setSaved] = useState(false);
  return (
    <Card className="mt-4 rounded-[22px] p-4 text-xs text-ink-500 shadow-none ring-1 ring-ink-900/[0.06]">
      <div className="rb-num flex items-center justify-between">
        <span>Runner Boy {isNative ? 'Android' : 'web'} · v{APP_VERSION}</span>
        {isNative && SERVER_OVERRIDE_ALLOWED && <Badge tone="accent">DEMO BUILD</Badge>}
      </div>
      {isNative && SERVER_OVERRIDE_ALLOWED && (
        <form
          className="mt-3 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setServerOverride(server.trim() || null);
            setSaved(true);
            setTimeout(() => window.location.reload(), 400);
          }}
        >
          <label htmlFor="srv" className="text-[11px] font-bold uppercase tracking-wide text-ink-500">
            {t('account.server', 'Server (demo builds only)')}
          </label>
          <Input id="srv" inputMode="url" value={server} onChange={(e) => setServer(e.target.value)} placeholder="https://…" />
          <Button size="sm" type="submit" variant="outline">
            {saved ? t('common.saved', 'Saved — restarting…') : t('common.save')}
          </Button>
        </form>
      )}
    </Card>
  );
}
